import React from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';

import styles from './WorkbenchFieldForm.css';
import tooltipStyles from './TooltipOverrides.css';
import toggleStyles from './ToggleOverrides.css';
import commonStyles from './common.css';
import WorkbenchFieldCampaignSelector from './WorkbenchFieldCampaignSelector';
import { getFieldTypeFromDisplayType } from './helpers';

import InformationIcon from 'zignal-landscape/lib/Icon/InformationIcon';
import Save1Icon from 'zignal-landscape/lib/Icon/Save1Icon';
import Remove1Icon from 'zignal-landscape/lib/Icon/Remove1Icon';
import Add2Icon from 'zignal-landscape/lib/Icon/Add2Icon';
import Link from 'zignal-landscape/lib/Link';
import Input from 'zignal-landscape/lib/Input';
import ToggleSwitch from 'zignal-landscape/lib/ToggleSwitch';
import Select from 'zignal-landscape/lib/Select';
import Tooltip from 'zignal-landscape/lib/ToolTip';
import Grid from 'zignal-landscape/lib/Grid';
import Row from 'zignal-landscape/lib/Row';
import Col from 'zignal-landscape/lib/Col';
// we are not using <GridContent /> so that we don't have to fight with its styling


const FIELD_TYPE_OPTIONS = [
  {value: 'Boolean', label: 'Boolean'},
  {value: 'Category', label: 'Category'},
  {value: 'Range', label: 'Range'},
  {value: 'Text', label: 'Text'}
];

function _initialState() {
  return {
    fieldCampaignIds: [],
    fieldIsAvailableToAllCampaigns: false,
    fieldName: '',
    fieldTypeForDisplay: '',
    fieldHasMultipleValues: false,
    showForm: false
  };
}

function _typeSupportsMultiValue(fieldTypeForDisplay) {
  return fieldTypeForDisplay === 'Category';
}


/**
 * TODO: clean up duplication with WorkbenchValueEditInput?
 */
class WorkbenchFieldForm extends React.Component {

  constructor(props) {
    super(props);

    this.handleNewFieldName = this.handleNewFieldName.bind(this);
    this.handleNewFieldType = this.handleNewFieldType.bind(this);
    this.handleAddFieldUpdate = this.handleAddFieldUpdate.bind(this);
    this.handleCampaignSelectorChangeCheckbox = this.handleCampaignSelectorChangeCheckbox.bind(this);
    this.handleCampaignSelectorChangeSelect = this.handleCampaignSelectorChangeSelect.bind(this);
    this.handleFormReset = this.handleFormReset.bind(this);

    this.state = _initialState();
  }

  handleNewFieldType(newFieldType) {
    this.setState({ fieldTypeForDisplay: newFieldType.value });
  }

  handleNewFieldName(event) {
    this.setState({
      fieldName: event.target.value
    });
  }

  handleCampaignSelectorChangeCheckbox(event) {
    const { fieldCampaignIds } = this.state;
    const checked = event.currentTarget.checked || false;
    this.setState({
      fieldIsAvailableToAllCampaigns: checked,
      fieldCampaignIds: (checked ? [] : fieldCampaignIds),
    });
  }

  handleCampaignSelectorChangeSelect(options) {
    const { fieldIsAvailableToAllCampaigns } = this.state;
    const campaignIds = options.map((option) => option.value);
    this.setState({
      fieldIsAvailableToAllCampaigns,
      fieldCampaignIds: (fieldIsAvailableToAllCampaigns ? [] : campaignIds),
    });
  }

  handleAddFieldUpdate(e) {
    e.stopPropagation();
    e.preventDefault();
    const fieldType = getFieldTypeFromDisplayType(this.state.fieldTypeForDisplay);
    const formattedState = Object.assign({}, this.state, { fieldType });
    this.props.onFieldAdd(formattedState, this.handleFormReset);
  }

  handleFormReset() {
    this.setState(_initialState());
  }

  renderMultipleValuesHeader(newFieldTypeForDisplay) {
    if (! _typeSupportsMultiValue(newFieldTypeForDisplay)) {
      return null;
    }
    return (<Col xs={2}>
      <span className={styles.multipleValuesHeader}>
        <span className={styles.multipleValuesText}>
          Multiple Values
        </span>
        <Tooltip
          position="bottom"
          customStyles={{
            tooltipText: tooltipStyles.tooltipText
          }}
          tooltipText="Allow selection of multiple values in Workbench"
        >
          <InformationIcon />
        </Tooltip>
      </span>
    </Col>);
  }

  renderMultipleValuesControl(newFieldTypeForDisplay) {
    if (! _typeSupportsMultiValue(newFieldTypeForDisplay)) {
      return null;
    }
    const { fieldHasMultipleValues } = this.state;
    return (<Col xs={2}>
      <span className={styles.multipleValuesToggle}>
        <ToggleSwitch
          className={toggleStyles.switch}
          enabled={fieldHasMultipleValues}
          id="hasMultipleValuesToggle"
          onChange={(e) => this.setState({fieldHasMultipleValues: e.target.checked})}
        />
      </span>
    </Col>);
  }

  renderCampaignSelectorHeader() {
    if (! this.props.isAccountLevel) {
      return null;
    }

    return (<Col xs={3}>
      <span className={styles.campaignSelectorHeader}>
        Share with Workspaces
      </span>
    </Col>);
  }

  renderCampaignSelectorControl() {
    if (! this.props.isAccountLevel) {
      return null;
    }

    const { campaigns } = this.props;
    const {
      fieldCampaignIds,
      fieldIsAvailableToAllCampaigns,
    } = this.state;

    return (<Col xs={4}>
      <span className={styles.campaignSelectorControl}>
        <WorkbenchFieldCampaignSelector
          allCampaigns={campaigns}
          campaignIds={fieldCampaignIds}
          isAvailableToAllCampaigns={fieldIsAvailableToAllCampaigns}
          onChangeCheckbox={this.handleCampaignSelectorChangeCheckbox}
          onChangeSelect={this.handleCampaignSelectorChangeSelect}
        />
      </span>
    </Col>);
  }

  renderForm() {
    const { isAccountLevel } = this.props;
    const { fieldTypeForDisplay, fieldName } = this.state;
    const formIsValid = fieldTypeForDisplay !== '' && fieldName !== '';
    const shouldFloatActions = isAccountLevel === true && fieldTypeForDisplay === 'Category';

    return (<div className={styles.formContainer}>
      <Grid fluid>
        <Row>
          <Col xs={3}>
            <span className={styles.fieldNameHeader}>Attribute Name</span>
          </Col>
          <Col xs={3}>
            <span className={styles.fieldTypeHeader}>Attribute Type</span>
          </Col>
          {this.renderMultipleValuesHeader(fieldTypeForDisplay)}
          {this.renderCampaignSelectorHeader()}
        </Row>
        <Row>
          <Col xs={3}>
            <form
              className={styles.fieldNameForm}
              onSubmit={this.handleAddFieldUpdate}
            >
              <Input
                autoFocus
                placeholder="Attribute Name"
                onChange={this.handleNewFieldName}
                value={fieldName}
              />
            </form>
          </Col>
          <Col xs={3}>
            <Select
              inline
              className={styles.fieldTypeSelect}
              value={find(FIELD_TYPE_OPTIONS, {value: fieldTypeForDisplay})}
              onChange={this.handleNewFieldType}
              placeholder="Select a Type"
              options={FIELD_TYPE_OPTIONS}
            />
          </Col>
          {this.renderMultipleValuesControl(fieldTypeForDisplay)}
          {this.renderCampaignSelectorControl()}
          <Col xs={2} className={shouldFloatActions ? styles.actionsFloatRight : null}>
            <span className={styles.actions}>
              <span
                className={formIsValid ? commonStyles.checkIsValid : commonStyles.check}
                onClick={this.handleAddFieldUpdate}
              >
                <Save1Icon />
              </span>
              <span
                className={commonStyles.close}
                onClick={this.handleFormReset}
              >
                <Remove1Icon />
              </span>
            </span>
          </Col>
        </Row>
      </Grid>
    </div>);
  }

  renderAddNewFieldButton() {
    return (<div className={styles.addNewFieldButton}>
      <Link
        icon={<Add2Icon />}
        onClick={() => {this.setState({showForm: true});}}
      >
        Add Attribute
      </Link>
    </div>);
  }

  render() {
    const { showForm } = this.state;
    return (showForm
      ? this.renderForm()
      : this.renderAddNewFieldButton()
    );
  }
}

WorkbenchFieldForm.propTypes = {
  isAccountLevel: PropTypes.bool.isRequired,
  campaigns: PropTypes.array.isRequired, // Model#attributes from 'campaigns' Bootstrap
  onFieldAdd: PropTypes.func,
};

WorkbenchFieldForm.defaultProps = {
};

export default WorkbenchFieldForm;
